package com.sweetlime.dungeonamigo.presentation.ui.components

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.size
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.StrokeJoin
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.unit.dp
import com.sweetlime.dungeonamigo.presentation.ui.theme.BrandColors
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin

@Composable
fun CharactersEmptyIllustration() {
    Canvas(modifier = Modifier.size(160.dp)) {
        val w = size.width
        val h = size.height
        val stroke = Stroke(width = 3f, cap = StrokeCap.Round, join = StrokeJoin.Round)
        val parchment = BrandColors.Parchment.copy(alpha = 0.7f)
        val dim = BrandColors.TextSecondary.copy(alpha = 0.5f)

        // Scroll body
        val scrollLeft = w * 0.18f
        val scrollRight = w * 0.82f
        val scrollTop = h * 0.15f
        val scrollBottom = h * 0.85f
        val scrollPath = Path().apply {
            addRoundRect(
                RoundRect(
                    rect = Rect(scrollLeft, scrollTop, scrollRight, scrollBottom),
                    cornerRadius = CornerRadius(12f, 12f),
                )
            )
        }
        drawPath(scrollPath, color = BrandColors.Surface)
        drawPath(scrollPath, color = parchment, style = stroke)

        // Scroll roll top
        drawOval(
            color = parchment,
            topLeft = Offset(scrollLeft - 12f, scrollTop - 14f),
            size = Size(scrollRight - scrollLeft + 24f, 28f),
            style = stroke,
        )
        // Scroll roll bottom
        drawOval(
            color = parchment,
            topLeft = Offset(scrollLeft - 12f, scrollBottom - 14f),
            size = Size(scrollRight - scrollLeft + 24f, 28f),
            style = stroke,
        )

        // Empty lines on scroll
        val lineStroke = Stroke(width = 2f, cap = StrokeCap.Round)
        val lineX1 = scrollLeft + 20f
        val lineX2 = scrollRight - 20f
        for (i in 0..3) {
            val y = scrollTop + 55f + i * 28f
            val lineColor = if (i == 0) dim.copy(alpha = 0.35f) else dim.copy(alpha = 0.25f)
            drawLine(lineColor, Offset(lineX1, y), Offset(lineX2, y), strokeWidth = 2f, cap = StrokeCap.Round)
        }

        // Quill pen
        val quillBase = Offset(w * 0.62f, h * 0.68f)
        val quillTip = Offset(w * 0.38f, h * 0.82f)
        drawLine(parchment, quillBase, quillTip, strokeWidth = 3f, cap = StrokeCap.Round)
        // Feather vanes
        val mid = Offset(
            (quillBase.x + quillTip.x) / 2f + 8f,
            (quillBase.y + quillTip.y) / 2f - 8f,
        )
        drawLine(parchment.copy(alpha = 0.5f), quillBase, mid, strokeWidth = 2f, cap = StrokeCap.Round)
        drawLine(parchment.copy(alpha = 0.4f), quillBase, Offset(mid.x + 6f, mid.y + 10f), strokeWidth = 2f, cap = StrokeCap.Round)
        drawLine(parchment.copy(alpha = 0.3f), quillBase, Offset(mid.x - 10f, mid.y + 6f), strokeWidth = 2f, cap = StrokeCap.Round)
    }
}

@Composable
fun SessionEmptyIllustration() {
    Canvas(modifier = Modifier.size(160.dp)) {
        val w = size.width
        val h = size.height
        val stroke = Stroke(width = 3f, cap = StrokeCap.Round, join = StrokeJoin.Round)
        val parchment = BrandColors.Parchment.copy(alpha = 0.7f)
        val surface = BrandColors.SurfaceLight

        // Round table (large oval)
        val tableCx = w / 2f
        val tableCy = h * 0.52f
        val tableRx = w * 0.32f
        val tableRy = h * 0.22f
        drawOval(
            color = surface,
            topLeft = Offset(tableCx - tableRx, tableCy - tableRy),
            size = Size(tableRx * 2f, tableRy * 2f),
        )
        drawOval(
            color = parchment,
            topLeft = Offset(tableCx - tableRx, tableCy - tableRy),
            size = Size(tableRx * 2f, tableRy * 2f),
            style = stroke,
        )

        // Empty chairs around table (5 positions)
        val chairAngles = listOf(0.0, 72.0, 144.0, 216.0, 288.0)
        val chairOrbitRx = tableRx + 28f
        val chairOrbitRy = tableRy + 28f
        chairAngles.forEach { angleDeg ->
            val rad = angleDeg * PI / 180.0
            val cx = (tableCx + chairOrbitRx * cos(rad)).toFloat()
            val cy = (tableCy + chairOrbitRy * sin(rad)).toFloat()
            // Chair back
            val chairW = 22f
            val chairH = 16f
            val dim = BrandColors.TextSecondary.copy(alpha = 0.4f)
            drawRoundRect(
                color = dim,
                topLeft = Offset(cx - chairW / 2f, cy - chairH / 2f),
                size = Size(chairW, chairH),
                cornerRadius = CornerRadius(4f, 4f),
                style = Stroke(width = 2f),
            )
        }

        // Candle in center of table
        drawRect(
            color = parchment.copy(alpha = 0.5f),
            topLeft = Offset(tableCx - 3f, tableCy - 14f),
            size = Size(6f, 14f),
        )
        // Flame
        val flamePath = Path().apply {
            moveTo(tableCx, tableCy - 14f)
            cubicTo(tableCx - 5f, tableCy - 22f, tableCx + 5f, tableCy - 26f, tableCx, tableCy - 28f)
            cubicTo(tableCx + 5f, tableCy - 26f, tableCx + 5f, tableCy - 22f, tableCx, tableCy - 14f)
        }
        drawPath(flamePath, color = Color(0xFFFFD700L).copy(alpha = 0.7f))
    }
}

@Composable
fun CompendiumSyncIllustration() {
    Canvas(modifier = Modifier.size(160.dp)) {
        val w = size.width
        val h = size.height
        val stroke = Stroke(width = 3f, cap = StrokeCap.Round, join = StrokeJoin.Round)
        val blue = BrandColors.SpellBlue.copy(alpha = 0.7f)
        val parchment = BrandColors.Parchment.copy(alpha = 0.7f)

        // Open book left page
        val bookBottom = h * 0.82f
        val bookTop = h * 0.2f
        val spine = w / 2f
        val leftEdge = w * 0.08f
        val rightEdge = w * 0.92f
        val bookH = bookBottom - bookTop
        val bookCorner = 8f

        val leftPage = Path().apply {
            moveTo(spine, bookTop)
            cubicTo(spine - bookH * 0.2f, bookTop - 10f, leftEdge + bookCorner, bookTop, leftEdge, bookTop + bookCorner)
            lineTo(leftEdge, bookBottom - bookCorner)
            quadraticTo(leftEdge, bookBottom, leftEdge + bookCorner, bookBottom)
            lineTo(spine, bookBottom)
            cubicTo(spine, bookBottom + 5f, spine, bookTop - 5f, spine, bookTop)
        }
        drawPath(leftPage, color = BrandColors.SurfaceLight)
        drawPath(leftPage, color = blue, style = stroke)

        val rightPage = Path().apply {
            moveTo(spine, bookTop)
            cubicTo(spine + bookH * 0.2f, bookTop - 10f, rightEdge - bookCorner, bookTop, rightEdge, bookTop + bookCorner)
            lineTo(rightEdge, bookBottom - bookCorner)
            quadraticTo(rightEdge, bookBottom, rightEdge - bookCorner, bookBottom)
            lineTo(spine, bookBottom)
            cubicTo(spine, bookBottom + 5f, spine, bookTop - 5f, spine, bookTop)
        }
        drawPath(rightPage, color = BrandColors.SurfaceLight)
        drawPath(rightPage, color = blue, style = stroke)

        // Text lines on left page
        val lineStrokeW = 2f
        val leftLineX1 = leftEdge + 16f
        val leftLineX2 = spine - 10f
        for (i in 0..4) {
            val y = bookTop + 30f + i * 20f
            val len = if (i == 4) 0.6f else 1f
            drawLine(
                parchment.copy(alpha = 0.4f),
                Offset(leftLineX1, y),
                Offset(leftLineX1 + (leftLineX2 - leftLineX1) * len, y),
                strokeWidth = lineStrokeW,
                cap = StrokeCap.Round,
            )
        }

        // Text lines on right page
        val rightLineX1 = spine + 10f
        val rightLineX2 = rightEdge - 16f
        for (i in 0..4) {
            val y = bookTop + 30f + i * 20f
            val len = if (i == 2) 0.5f else 1f
            drawLine(
                parchment.copy(alpha = 0.4f),
                Offset(rightLineX1, y),
                Offset(rightLineX1 + (rightLineX2 - rightLineX1) * len, y),
                strokeWidth = lineStrokeW,
                cap = StrokeCap.Round,
            )
        }

        // Spine line
        drawLine(blue.copy(alpha = 0.5f), Offset(spine, bookTop), Offset(spine, bookBottom), strokeWidth = 2f)
    }
}

@Composable
fun ProfileEmptyCharactersIllustration() {
    Canvas(modifier = Modifier.size(160.dp)) {
        val w = size.width
        val h = size.height
        val stroke = Stroke(width = 3f, cap = StrokeCap.Round, join = StrokeJoin.Round)
        val dim = BrandColors.TextSecondary.copy(alpha = 0.5f)

        // Shield outline
        val shieldLeft = w * 0.2f
        val shieldRight = w * 0.8f
        val shieldTop = h * 0.1f
        val shieldW = shieldRight - shieldLeft
        val cx = w / 2f

        val shieldPath = Path().apply {
            moveTo(shieldLeft, shieldTop)
            lineTo(shieldRight, shieldTop)
            lineTo(shieldRight, h * 0.6f)
            cubicTo(shieldRight, h * 0.75f, cx + shieldW * 0.15f, h * 0.88f, cx, h * 0.9f)
            cubicTo(cx - shieldW * 0.15f, h * 0.88f, shieldLeft, h * 0.75f, shieldLeft, h * 0.6f)
            close()
        }
        drawPath(shieldPath, color = BrandColors.Surface)
        drawPath(shieldPath, color = dim, style = stroke)

        // Shield horizontal divider
        drawLine(dim.copy(alpha = 0.4f), Offset(shieldLeft, shieldTop + (h * 0.6f - shieldTop) / 2f), Offset(shieldRight, shieldTop + (h * 0.6f - shieldTop) / 2f), strokeWidth = 2f)

        // Shield vertical divider
        val midY = shieldTop + (h * 0.9f - shieldTop) / 2f
        drawLine(dim.copy(alpha = 0.4f), Offset(cx, shieldTop), Offset(cx, midY + 10f), strokeWidth = 2f)

        // Question mark / blank crest
        val crestCy = shieldTop + (h * 0.52f - shieldTop) / 2f
        val crestCx = cx
        // Small diamond crest placeholder
        val crestR = 16f
        val crestPath = Path().apply {
            moveTo(crestCx, crestCy - crestR)
            lineTo(crestCx + crestR, crestCy)
            lineTo(crestCx, crestCy + crestR)
            lineTo(crestCx - crestR, crestCy)
            close()
        }
        drawPath(crestPath, color = dim.copy(alpha = 0.3f))
        drawPath(crestPath, color = dim, style = Stroke(width = 2f))
    }
}
